import React, { useCallback, useMemo } from 'react';

import { useGetIssuersList } from 'lib/hooks/queries/eprescribe/useGetIssuersList';
import { useGetLicenseTypes } from 'lib/hooks/queries/license/useGetLicenseTypes';

import Checkbox from 'components/shared/Checkbox/Checkbox';
import Popover from 'components/shared/Popovers/Popover/Popover';

export default function useLicenseColumns({ selectedLicense, setSelectedLicense, onEditLicense, isAdmin }) {
  const { data: issuersData } = useGetIssuersList();
  const { data: licenseTypesData } = useGetLicenseTypes();

  const getIssuerName = useCallback(
    (issuerId) => issuersData?.issuers?.find((i) => i.id === issuerId)?.name || '-',
    [issuersData]
  );

  const getLicenseTypeName = useCallback(
    (licenseId) => licenseTypesData?.licenses?.find((l) => l.id === licenseId)?.name || '-',
    [licenseTypesData]
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const RadioSelectCellRenderer = useCallback(
    ({ data }) => {
      const isSelected = selectedLicense?.id === data?.id;
      return (
        <div className="flex h-full items-center">
          <Checkbox
            id={`license-${data?.id}`}
            label=""
            rounded
            size={16}
            isChecked={isSelected}
            onChange={() => setSelectedLicense(data)}
          />
        </div>
      );
    },
    [selectedLicense, setSelectedLicense]
  );

  const columnDefs = useMemo(() => {
    const baseColumns = [
      {
        field: 'selection',
        headerName: '',
        width: 56,
        cellRenderer: RadioSelectCellRenderer,
        cellClass: '!flex !items-center !justify-center',
        sortable: false,
        suppressMenu: true,
        resizable: false
      },
      {
        field: 'issuer',
        headerName: 'Issuer',
        cellClass: '!flex !items-center',
        valueGetter: ({ data }) =>
          `${getLicenseTypeName(data?.license_id)} / ${getIssuerName(data?.issuer_id)}`,
        flex: 1.5
      },
      {
        field: 'license_number',
        headerName: 'License number',
        cellClass: '!flex !items-center',
        flex: 1
      },
      {
        field: 'expired_on',
        headerName: 'Expiration date',
        cellClass: '!flex !items-center',
        valueFormatter: ({ value }) => formatDate(value),
        flex: 1
      }
    ];

    if (isAdmin) {
      baseColumns.push({
        field: 'actions',
        headerName: 'Actions',
        cellClass: '!flex !items-center !justify-center',
        cellRenderer: ({ data }) => (
          <Popover
            className="flex h-full w-full items-center justify-center"
            position="left"
            isFixed
            icon="context-menu-horizontal"
            options={[
              {
                label: 'Edit',
                icon: 'edit-4-mono',
                onClick: () => onEditLicense?.(data)
              }
            ]}
            isBorder={false}
          />
        ),
        width: 80,
        sortable: false
      });
    }

    return baseColumns;
  }, [RadioSelectCellRenderer, getIssuerName, getLicenseTypeName, onEditLicense, isAdmin]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMenu: true
    }),
    []
  );

  return { columnDefs, defaultColDef };
}
